import { format } from 'date-fns';
import type { Booking } from '@/types';
import { getBookingSegments, type BookingTimingFields } from './booking-conflict';

const keyToDate = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const isoToTimeLabel = (iso: string): string => {
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '00:00';
};

const isoToDateKey = (iso: string): string | null => {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const halfDayLabel = (booking: BookingTimingFields): string | null => {
  if (booking.durationType === 'half-day-am') return 'Morning';
  if (booking.durationType === 'half-day-pm') return 'Afternoon';
  if (booking.durationType === 'half-day') {
    const period = booking.halfDayPeriod
      ?? (Number(isoToTimeLabel(booking.startTime).slice(0, 2)) < 12 ? 'am' : 'pm');
    return period === 'am' ? 'Morning' : 'Afternoon';
  }
  return null;
};

/**
 * Compact label for when a booking happens: the calendar days it actually
 * occupies, with a clock range only for hourly bookings and an AM/PM word
 * for half-days.
 */
export function formatBookingWhen(booking: BookingTimingFields, dateFormat = 'dd MMM'): string {
  const segments = getBookingSegments(booking as Booking);
  const startKey = segments[0]?.dateKey ?? isoToDateKey(booking.startTime);
  const endKey = segments[segments.length - 1]?.dateKey ?? startKey;
  if (!startKey || !endKey) return '';
  const dateStr = startKey === endKey
    ? format(keyToDate(startKey), dateFormat)
    : `${format(keyToDate(startKey), dateFormat)}–${format(keyToDate(endKey), dateFormat)}`;
  const startLabel = isoToTimeLabel(booking.startTime);
  const endLabel = isoToTimeLabel(booking.endTime);
  // An hourly booking running midnight-to-midnight (or to 23:59) is a
  // whole-day span when no duration type reached the API.
  const spansWholeDays = startLabel === '00:00' && (endLabel === '00:00' || endLabel === '23:59');
  if (booking.durationType === 'hourly' && !spansWholeDays) {
    return `${dateStr} · ${startLabel}–${endLabel}`;
  }
  const half = halfDayLabel(booking);
  return half ? `${dateStr} · ${half}` : dateStr;
}
